import PropTypes from 'prop-types';
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { IoIosArrowBack } from 'react-icons/io';
import TextareaAutosize from 'react-textarea-autosize';

import {
    Avatar,
    ConversationTypes,
    GenderSelect,
    IconButton,
    InlineEdit,
    Input,
    Scrollbar,
    TagsInput,
    useAutoSave,
    useBreakpoints,
    useEnhancedFormik,
} from '@mssgme/ui';

import { AssignTo, useAssignTo } from '../AssignTo';
import useClientCard from './useClientCard';

import styles from './ClientCard.scss';

const FIELDS = ['name', 'email', 'phone', 'gender', 'address', 'notes', 'tags'];

PureClientCard.defaultProps = {
    visible: false,
};

PureClientCard.propTypes = {
    className: PropTypes.string,
    assignTo: PropTypes.shape(AssignTo.propTypes).isRequired,
    contact: ConversationTypes.contact.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onClose: PropTypes.func,
};

// TODO: refactor
export function PureClientCard({ className, assignTo, contact, onSubmit, onClose }) {
    const { t } = useTranslation('inbox');
    const { isMobile } = useBreakpoints();
    const initialValues = useMemo(() => getInitialValues(contact), [contact]);
    const formik = useEnhancedFormik({
        initialValues,
        onSubmit,
    });

    const { success, getFieldProps } = useAutoSave(formik);

    const handleTagsChange = (value) => {
        formik.setFieldValue('tags', value);
        formik.submitForm();
    };

    return (
        <Scrollbar>
            <div className={classNames(styles.root, className)}>
                {isMobile && (
                    <header className={styles.nav}>
                        <IconButton onClick={onClose} className={styles.backBtn}>
                            <IoIosArrowBack size={24} />
                        </IconButton>
                    </header>
                )}
                <form>
                    <section className={styles.header}>
                        <Avatar className={styles.avatar} size={100} name={contact.name} src={contact.avatarUrl} />
                        <h4 className={styles.userName}>{contact.name}</h4>
                    </section>
                    <section className={styles.content}>
                        <h6 className={styles.sectionHeading}>{t('ClientCard.headings.infoHeading')}</h6>
                        <ul className={styles.infoList}>
                            {FIELDS.filter((name) => name !== 'notes' && name !== 'tags').map((name) => {
                                const tagsMap = {
                                    gender: GenderSelect,
                                    // country: CountrySelect,
                                };

                                return (
                                    <li key={name}>
                                        <InlineEdit
                                            {...getFieldProps(name)}
                                            id={`client_${name}`}
                                            tag={tagsMap[name] || Input}
                                            successfully={success[name]}
                                            label={t(`ClientCard.inputs.${name}.label`)}
                                            placeholder={t(`ClientCard.inputs.${name}.placeholder`)}
                                            autoComplete="off"
                                        />
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                    <section className={styles.content}>
                        <h6 className={styles.sectionHeading}>{t('ClientCard.headings.assignTo')}</h6>
                        <AssignTo {...assignTo} />
                    </section>
                    <section className={styles.content}>
                        <h6 className={styles.sectionHeading}>{t('ClientCard.headings.tags')}</h6>
                        <TagsInput value={formik.values.tags} onChange={handleTagsChange} error={formik.errors.tags} />
                    </section>
                    <section className={styles.content}>
                        <h6 className={styles.sectionHeading}>{t('ClientCard.headings.notesHeading')}</h6>
                        <InlineEdit
                            {...getFieldProps('notes')}
                            successfully={success.notes}
                            tag={TextareaAutosize}
                            placeholder={t('ClientCard.inputs.notes.placeholder')}
                            autoComplete="off"
                            indicatorPosition="end"
                            maxRows={Infinity}
                        />
                    </section>
                </form>
            </div>
        </Scrollbar>
    );
}

function getInitialValues(contact) {
    const getDefaultValue = (name) => (name === 'tags' ? [] : '');

    return FIELDS.reduce((acc, name) => {
        acc[name] = contact[name] || getDefaultValue(name);
        return acc;
    }, {});
}

export default function ClientCard(props) {
    const { contactId } = useParams();
    const state = useClientCard(contactId);
    const assignTo = useAssignTo(contactId);

    return <PureClientCard {...state} assignTo={assignTo} {...props} />;
}
